import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { PageLayout, Clause, Callout, Bullets } from "@/components/PageLayout";
import { btnPrimary } from "@/components/Brand";

/**
 * Long-form narrative copy, held per language in the page itself rather than in the
 * shared i18n dictionary — prose this long is unmanageable as flat key/value pairs.
 */
const COPY = {
  en: {
    eyebrow: "About Seleksa",
    title: "Built in a busy studio, not a boardroom.",
    intro:
      "Seleksa exists because a photography studio in Medan was drowning in spreadsheets, WhatsApp screenshots and folders named 'final_final_revisi_2'. We built the tool we needed, then made it work for everyone else.",
    numbers: [
      { figure: "60+", label: "Graduation sessions handled in a single day" },
      { figure: "3", label: "Steps for a client to finish choosing" },
      { figure: "0", label: "Original files uploaded to our servers" },
    ],
    c1: {
      title: "It started with graduation season",
      p1: "Anyone who has run a studio through graduation season in Indonesia knows the specific kind of chaos it brings. Not the photography — that part is the easy part. The chaos is administrative.",
      p2: "At Sudut Studio in Medan, a single day could mean more than sixty graduation sessions. Sixty families, each with a package: a certain number of edited photographs, a certain allocation of prints in specific sizes, some framed, some not. Sixty sets of selections to collect, verify, price and route to the right editor and the right printer.",
      p3: "The workflow was a patchwork held together by willpower. Clients would send filenames over WhatsApp, often as screenshots, sometimes as voice notes. A staff member would transcribe them into a spreadsheet. Somebody would count whether the client had exceeded their quota, and either chase the difference or quietly absorb the loss. Then someone would sit down and manually drag files out of a folder of two thousand images into another folder, one photograph at a time, matching names by eye at eleven at night.",
      callout: "The bottleneck was never the camera. It was the hours between a client choosing a photograph and an editor being able to open the right file.",
    },
    c2: {
      title: "What kept going wrong",
      lead: "The failures were always the same handful, and every one of them was expensive:",
      bullets: [
        "Wrong file, right name. Two photographs a digit apart in filename, and the editor works on the one the client did not choose. Discovered after printing.",
        "Silent quota overruns. A client picks thirty-one photographs on a twenty-five photograph package, nobody counts carefully, and the studio eats six edits.",
        "Unbillable extras. Extra prints and frames agreed verbally over chat, never recorded, never invoiced.",
        "The transcription tax. Hours of skilled staff time spent retyping filenames from a screenshot into a spreadsheet.",
        "No paper trail. When a client insisted they had asked for something different, there was nothing to check.",
      ],
      close: "None of this is a photography problem. It is a data-entry problem wearing a photography costume — and data-entry problems are precisely what software is for.",
    },
    c3: {
      title: "The Magic Seleksa Tools",
      lead: "We had one design rule: a studio should never retype anything a client has already told us, and a client should never have to learn how to use software. Everything we build is measured against that.",
      bullets: [
        "One private link. The client opens it on their phone, taps the photographs they want, and sees immediately when they have passed their included quota and what the extras will cost.",
        "Quotas that enforce themselves. Package allocations count down in front of the client, so a thirty-first selection on a twenty-five photograph package is a priced decision, not a surprise.",
        "Notes attached to the photograph. A revision request lives on the image it refers to, not in a chat thread three days deep.",
        "The Auto-Sorter. Seleksa generates a script the studio runs on their own machine that finds every selected file — RAW, JPG and sidecar — and sorts it into clean, ready-to-edit folders. The overnight dragging session is gone.",
        "A priced summary the moment the client submits. Quotas, extras, frames and custom charges resolved into an invoice, and a PDF the studio can send straight on.",
      ],
    },
    c4: {
      title: "Why we refuse to hold your originals",
      p1: "Early on we made an architectural decision that still shapes the product: Seleksa does not take custody of a studio's high-resolution files.",
      p2: "The temptation to do so is obvious — it would make some features easier to build. But it would also mean a studio waiting on an upload before a client could even start, paying for storage of files they already own, and trusting somebody else's server with the only copy of a wedding.",
      callout: "Your originals stay in your storage. Seleksa works with lightweight web previews for choosing, and the sorting happens on your own computer. We call it Zero-Server-Load, and it is the reason the product is fast and the reason our Privacy Policy is short.",
    },
    c5: {
      title: "Where we are, and where we are going",
      p1: "Seleksa is built and run from Medan, North Sumatra. That matters more than it might sound: it means the product is designed around Indonesian realities — QRIS and virtual-account payments through Midtrans, WhatsApp as the default channel for reaching a client, print sizes and framing conventions as local studios actually quote them, and an interface that works in Bahasa Indonesia first.",
      p2: "We are still a small team, and we still test every feature against the same question we started with: does this remove administrative work from a studio's evening? If it does not, it does not ship.",
      cta: "Start a free trial",
    },
  },
  id: {
    eyebrow: "Tentang Seleksa",
    title: "Lahir di studio yang sibuk, bukan di ruang rapat.",
    intro:
      "Seleksa ada karena sebuah studio foto di Medan kewalahan dengan spreadsheet, tangkapan layar WhatsApp, dan folder bernama 'final_final_revisi_2'. Kami membuat alat yang kami butuhkan sendiri, lalu merapikannya untuk studio lain.",
    numbers: [
      { figure: "60+", label: "Sesi wisuda ditangani dalam satu hari" },
      { figure: "3", label: "Langkah bagi klien untuk menyelesaikan pilihan" },
      { figure: "0", label: "File asli yang diunggah ke server kami" },
    ],
    c1: {
      title: "Semua dimulai dari musim wisuda",
      p1: "Siapa pun yang pernah menjalankan studio saat musim wisuda di Indonesia tahu kekacauan macam apa yang datang. Bukan soal memotretnya — itu bagian yang mudah. Kekacauannya ada di administrasi.",
      p2: "Di Sudut Studio Medan, satu hari bisa berarti lebih dari enam puluh sesi wisuda. Enam puluh keluarga, masing-masing dengan paketnya: sejumlah foto yang diedit, sejumlah cetakan dalam ukuran tertentu, sebagian dipigura, sebagian tidak. Enam puluh set pilihan yang harus dikumpulkan, diperiksa, dihitung harganya, lalu diteruskan ke editor dan tukang cetak yang tepat.",
      p3: "Alur kerjanya tambal sulam yang bertahan karena tenaga manusia. Klien mengirim nama file lewat WhatsApp, sering berupa tangkapan layar, kadang pesan suara. Staf menyalinnya satu-satu ke spreadsheet. Ada yang menghitung apakah klien melewati kuotanya, lalu menagih selisihnya atau diam-diam menanggung kerugiannya. Setelah itu seseorang duduk menyeret file dari folder berisi dua ribu foto ke folder lain, satu per satu, mencocokkan nama dengan mata pada pukul sebelas malam.",
      callout: "Hambatannya sama sekali bukan kamera. Hambatannya adalah jam-jam antara klien memilih foto dan editor bisa membuka file yang benar.",
    },
    c2: {
      title: "Kesalahan yang terus berulang",
      lead: "Kegagalannya selalu segelintir yang sama, dan semuanya mahal:",
      bullets: [
        "Salah file, nama mirip. Dua foto yang namanya beda satu angka, lalu editor mengerjakan foto yang tidak dipilih klien. Baru ketahuan setelah dicetak.",
        "Kuota terlewat tanpa sadar. Klien memilih tiga puluh satu foto pada paket dua puluh lima foto, tidak ada yang menghitung teliti, dan studio menanggung enam editan.",
        "Tambahan yang tak tertagih. Cetak dan pigura tambahan disepakati lewat obrolan, tidak pernah dicatat, tidak pernah masuk invoice.",
        "Pajak menyalin data. Berjam-jam waktu staf terampil habis untuk mengetik ulang nama file dari tangkapan layar ke spreadsheet.",
        "Tidak ada jejak. Ketika klien bersikeras pesanannya berbeda, tidak ada yang bisa dicek.",
      ],
      close: "Tidak satu pun dari ini masalah fotografi. Ini masalah entri data yang berkostum fotografi — dan masalah entri data justru itulah gunanya perangkat lunak.",
    },
    c3: {
      title: "Magic Seleksa Tools",
      lead: "Kami punya satu aturan desain: studio tidak boleh mengetik ulang apa pun yang sudah disampaikan klien, dan klien tidak perlu belajar memakai aplikasi. Semua yang kami bangun diukur dengan itu.",
      bullets: [
        "Satu tautan pribadi. Klien membukanya di ponsel, mengetuk foto yang diinginkan, dan langsung melihat kapan kuota paketnya terlewat serta berapa biaya tambahannya.",
        "Kuota yang menjaga dirinya sendiri. Jatah paket berkurang di depan mata klien, jadi pilihan ke tiga puluh satu pada paket dua puluh lima foto menjadi keputusan berharga, bukan kejutan.",
        "Catatan menempel pada fotonya. Permintaan revisi tinggal di foto yang dimaksud, bukan terkubur di obrolan tiga hari lalu.",
        "Auto-Sorter. Seleksa membuat skrip yang dijalankan studio di komputernya sendiri untuk menemukan setiap file terpilih — RAW, JPG, dan sidecar — lalu merapikannya ke folder yang siap diedit. Sesi menyeret file sampai malam pun selesai.",
        "Ringkasan berharga begitu klien mengirim. Kuota, tambahan, pigura, dan biaya khusus langsung menjadi invoice, plus PDF yang bisa studio teruskan.",
      ],
    },
    c4: {
      title: "Mengapa kami menolak menyimpan file asli Anda",
      p1: "Sejak awal kami mengambil keputusan arsitektur yang masih membentuk produk ini: Seleksa tidak mengambil alih penyimpanan file resolusi tinggi milik studio.",
      p2: "Godaannya jelas — beberapa fitur jadi lebih mudah dibangun. Tetapi itu juga berarti studio harus menunggu unggahan selesai sebelum klien bisa mulai memilih, membayar penyimpanan untuk file yang sudah mereka miliki, dan menitipkan satu-satunya salinan foto pernikahan ke server orang lain.",
      callout: "File asli Anda tetap di penyimpanan Anda. Seleksa hanya memakai pratinjau web yang ringan untuk memilih, dan penyortiran terjadi di komputer Anda sendiri. Kami menyebutnya Zero-Server-Load — itulah alasan produk ini cepat dan alasan Kebijakan Privasi kami pendek.",
    },
    c5: {
      title: "Di mana kami sekarang, dan ke mana kami menuju",
      p1: "Seleksa dibangun dan dijalankan dari Medan, Sumatera Utara. Ini lebih berarti daripada kelihatannya: produk ini dirancang mengikuti kenyataan di Indonesia — pembayaran QRIS dan virtual account lewat Midtrans, WhatsApp sebagai jalur utama menghubungi klien, ukuran cetak dan kebiasaan pigura sesuai cara studio lokal menghitungnya, serta antarmuka yang mengutamakan Bahasa Indonesia.",
      p2: "Tim kami masih kecil, dan setiap fitur tetap kami uji dengan pertanyaan yang sama sejak awal: apakah ini mengurangi pekerjaan administrasi di malam hari sebuah studio? Kalau tidak, fitur itu tidak kami rilis.",
      cta: "Mulai uji coba gratis",
    },
  },
};

export default function AboutUs() {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.en;

  return (
    <PageLayout testId="about-us-page" eyebrow={c.eyebrow} title={c.title} intro={c.intro}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#2A2A2A] border border-[#2A2A2A] mb-16"
        data-testid="about-numbers">
        {c.numbers.map((n) => (
          <div key={n.label} className="bg-[#0B0B0B] p-8">
            <p className="font-mono-accent text-3xl sm:text-4xl text-[#D4AF37]">{n.figure}</p>
            <p className="mt-4 text-sm text-[#A8A8A8] leading-relaxed">{n.label}</p>
          </div>
        ))}
      </div>

      <Clause n={1} title={c.c1.title}>
        <p>{c.c1.p1}</p>
        <p>{c.c1.p2}</p>
        <p>{c.c1.p3}</p>
        <Callout>{c.c1.callout}</Callout>
      </Clause>

      <Clause n={2} title={c.c2.title}>
        <p>{c.c2.lead}</p>
        <Bullets items={c.c2.bullets} />
        <p>{c.c2.close}</p>
      </Clause>

      <Clause n={3} title={c.c3.title}>
        <p>{c.c3.lead}</p>
        <Bullets items={c.c3.bullets} />
      </Clause>

      <Clause n={4} title={c.c4.title}>
        <p>{c.c4.p1}</p>
        <p>{c.c4.p2}</p>
        <Callout>{c.c4.callout}</Callout>
      </Clause>

      <Clause n={5} title={c.c5.title}>
        <p>{c.c5.p1}</p>
        <p>{c.c5.p2}</p>
        <div className="pt-4">
          <Link to="/login" data-testid="about-cta-btn" className={btnPrimary}>
            {c.c5.cta} <ArrowRight size={15} />
          </Link>
        </div>
      </Clause>
    </PageLayout>
  );
}
